import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, Star, Check, Minus, ArrowRight, MessageCircle, CalendarDays } from "lucide-react";
import { formatINR } from "../lib/api";
import { lockScroll } from "../lib/scrollLock";
import { BUSINESS, TRIP_INCLUSIONS, TRIP_EXCLUSIONS, bookWaMessage, departureLabel, durationLabel, waLink } from "../data/packages";
import { useEnquiry, EASE } from "./EnquiryModal";

export default function PackageModal({ pkg, onClose, tripType = "Domestic Package" }) {
  const { openEnquiry } = useEnquiry();

  useEffect(() => lockScroll(), []);

  const onEnquire = () => {
    onClose();
    openEnquiry({ tripType, destination: pkg.name });
  };

  return (
    <motion.div
      data-testid="package-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[130] flex items-end justify-center sm:items-center sm:p-6"
    >
      <div className="absolute inset-0 bg-ink/85 backdrop-blur-md" onClick={onClose} />
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.98 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.45, ease: EASE }}
        className="relative z-10 max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl border border-white/10 bg-obsidian shadow-2xl sm:rounded-3xl"
      >
        <div className="relative h-52 md:h-64">
          <img src={pkg.image} alt={pkg.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
          <button
            data-testid="package-modal-close-btn"
            aria-label="Close"
            onClick={onClose}
            className="glass absolute right-5 top-5 rounded-full p-2.5 text-slate-100 transition-colors hover:text-aurora"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-5 left-6 right-6 md:left-9">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">{pkg.region}</p>
            <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tight text-slate-50 md:text-3xl">{pkg.name}</h3>
          </div>
        </div>

        <div className="p-6 md:p-9">
          <div className="flex flex-wrap items-center gap-2.5">
            {[durationLabel(pkg), pkg.rating ? `★ ${pkg.rating} (${pkg.reviews} reviews)` : null, ...(pkg.highlights || [])].filter(Boolean).map((chip, i) => (
              <span key={i} className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-slate-300">
                {chip}
              </span>
            ))}
          </div>

          <p className="mt-5 font-editorial text-lg italic leading-relaxed text-slate-300">{pkg.tagline}</p>
          <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-aurora" data-testid="package-modal-departure">
            <CalendarDays className="h-4 w-4" /> Departs every {departureLabel(pkg)}
          </p>

          {(pkg.itinerary || []).length > 0 && (
            <>
              <h4 className="mt-9 font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">Day by Day</h4>
              <div className="mt-5 space-y-0">
                {pkg.itinerary.map((d) => (
                  <div key={d.day} className="flex gap-5 border-b border-white/5 py-4 last:border-0">
                    <span className="w-16 shrink-0 pt-0.5 font-mono text-[10px] tracking-[0.2em] text-comet">
                      DAY {String(d.day).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-display text-sm font-bold text-slate-100 md:text-base">{d.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-slate-400">{d.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="mt-9 grid gap-8 sm:grid-cols-2">
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.3em] text-aurora">What's Included</h4>
              <ul className="mt-4 space-y-2.5">
                {TRIP_INCLUSIONS.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-aurora" /> {it}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Not Included</h4>
              <ul className="mt-4 space-y-2.5">
                {TRIP_EXCLUSIONS.map((it) => (
                  <li key={it} className="flex items-start gap-2.5 text-sm text-slate-400">
                    <Minus className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" /> {it}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-9 flex flex-col items-start justify-between gap-5 rounded-2xl border border-aurora/25 bg-aurora/5 p-6 sm:flex-row sm:items-center">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Starting from</p>
              <p className="mt-1 font-display text-2xl font-extrabold text-slate-50">
                {formatINR(pkg.price)}
                <span className="font-body text-xs font-normal text-slate-400"> /person (twin sharing)</span>
              </p>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
              <a
                data-testid="package-whatsapp-book-btn"
                href={waLink(bookWaMessage(pkg))}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-bold text-[#06281a] transition-all duration-300 hover:shadow-[0_0_35px_rgba(37,211,102,0.4)] hover:-translate-y-0.5"
              >
                Book on WhatsApp <MessageCircle className="h-4 w-4" />
              </a>
              <button
                data-testid="package-enquire-btn"
                onClick={onEnquire}
                className="flex items-center justify-center gap-2 rounded-full border border-aurora/50 px-7 py-3.5 text-sm font-bold text-aurora transition-all duration-300 hover:bg-aurora hover:text-ink"
              >
                Send Enquiry <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">
            Pay easily via UPI or bank transfer — payment details shared instantly on WhatsApp
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
